import { Howl } from 'howler'

class SoundService {
  SOUNDS = {
    bgmMenu: { type: 'bgm', fileName: 'bgm_yuttari.mp3' },
    bgmChallenge: { type: 'bgm', fileName: 'bgm_challenge.mp3' },
    bgmLesson: { type: 'bgm', fileName: 'bgm_lesson.mp3' },
    bgmCongrats: { type: 'bgm', fileName: 'bgm_congrats.mp3' },
    menuClick: { type: 'effect', fileName: 'decision7.mp3' },
    menuClick2: { type: 'effect', fileName: 'menuClick.mp3' },
    menuHover: { type: 'effect', fileName: 'cursor1.mp3' },
    lessonStageSelect: { type: 'effect', fileName: 'menuClick.mp3' },
    fail: { type: 'effect', fileName: 'boo.mp3' },
    keyUpper: { type: 'effect', fileName: 'poka.mp3' },
    key: { type: 'effect', fileName: 'poka.mp3' },
    keyLower: { type: 'effect', fileName: 'poka.mp3' },
    vanish: { type: 'effect', fileName: 'swish1.mp3' },
    vowel: { type: 'effect', fileName: 'poka_high.mp3' }, // or boo
    goal: { type: 'effect', fileName: 'blackout_harp1.mp3' },
    challengeStart: { type: 'effect', fileName: 'hyoushigi1.mp3' },
    challengeGoal: { type: 'effect', fileName: 'hyoushigi2.mp3' },
    clear: { type: 'effect', fileName: 'kira1.mp3' }
  }

  sounds = {}

  msg = null

  isBGMMuted = false
  isEffectMuted = false

  _waitLoad(soundName) {
    return new Promise(async resolve => {
      if (this.sounds[soundName] && this.sounds[soundName].ready) {
        resolve()
        return
      }
      if (!this.sounds[soundName]) {
        await new Promise(resolve => {
          const timer = setInterval(() => {
            if (this.sounds[soundName]) {
              clearInterval(timer)
              resolve()
            }
          }, 100)
        })
      }
      if (this.sounds[soundName].sound.state() === 'loaded') {
        this.sounds[soundName].ready = true
        resolve()
      }
      this.sounds[soundName].sound.once('load', () => {
        this.sounds[soundName].ready = true
        resolve()
      })
    })
  }

  /**
   * すべてのAudioInstanceを事前作成する
   */
  init({ isEffectSoundMuted, isBGMSoundMuted }) {
    this.isEffectMuted = isEffectSoundMuted
    this.isBGMMuted = isBGMSoundMuted
    Object.keys(this.SOUNDS).forEach(key => {
      this.sounds[key] = {
        type: this.SOUNDS[key].type,
        sound: new Howl({
          src: `/app/static/sounds/${this.SOUNDS[key].fileName}`,
          loop: this.SOUNDS[key].type === 'bgm',
          volume: 0.2
        }),
        ready: false
      }
    })
  }

  /**
   * 事前作成したインスタンスでの音声再生
   */
  async play(soundName) {
    if (!this.sounds[soundName] || !this.sounds[soundName].ready)
      await this._waitLoad(soundName) // 読み込み待ち
    if (this.isEffectMuted) return
    this.sounds[soundName].sound.play()
  }

  async bgmPlay(soundName) {
    if (!this.sounds[soundName] || !this.sounds[soundName].ready)
      await this._waitLoad(soundName) // 読み込み待ち
    if (this.isBGMMuted) return
    if (!this.sounds[soundName].sound.playing()) {
      this.sounds[soundName].sound.play()
    }
  }

  stop(soundName) {
    if (this.sounds[soundName] && this.sounds[soundName].sound.playing()) {
      this.sounds[soundName].sound.stop()
    }
  }

  stopAll(type) {
    if (!Object.keys(this.sounds).length) return
    Object.keys(this.sounds).forEach(key => {
      if (!type) {
        this.sounds[key].sound.stop()
      } else if (this.sounds[key].type === type) {
        this.sounds[key].sound.stop()
      }
    })
  }

  speak(message, rate = 1.1) {
    if (this.isEffectMuted) return Promise.resolve()
    return new Promise(resolve => {
      this.msg = new SpeechSynthesisUtterance()
      this.msg.volume = 0.5 // 音量 min 0 ~ max 1
      this.msg.rate = rate // 速度 min 0 ~ max 10
      this.msg.pitch = 0.5 // 音程 min 0 ~ max 2
      this.msg.text = message // 喋る内容
      this.msg.lang = 'ja-JP' // en-US or ja-JP
      speechSynthesis.cancel()
      speechSynthesis.speak(this.msg)

      setInterval(() => {
        if (speechSynthesis.speaking === false) resolve()
      }, 100)
    })
  }

  stopSpeech() {
    return new Promise(resolve => {
      speechSynthesis.cancel()
      setInterval(() => {
        if (speechSynthesis.speaking === false) resolve()
      }, 100)
    })
  }

  initCustomInstance(soundList = {}) {
    const instance = {}
    Object.keys(soundList).forEach(key => {
      instance[key] = {
        type: soundList[key].type,
        sound: new Howl({
          src: `${soundList[key].fileName}`,
          loop: soundList[key].type === 'bgm',
          volume: 0.2
        }),
        ready: false
      }
    })
    return instance
  }

  _waitCustomInstanceLoad(soundName, instance) {
    return new Promise(async resolve => {
      if (instance[soundName] && instance[soundName].ready) {
        resolve()
        return
      }
      if (!instance[soundName]) {
        await new Promise(resolve => {
          const timer = setInterval(() => {
            if (instance[soundName]) {
              clearInterval(timer)
              resolve()
            }
          }, 100)
        })
      }
      if (instance[soundName].sound.state() === 'loaded') {
        instance[soundName].ready = true
        resolve()
      }
      instance[soundName].sound.once('load', () => {
        instance[soundName].ready = true
        resolve()
      })
    })
  }

  /**
   * 事前作成したインスタンスでの音声再生
   */
  async playCustomSound(soundName, instance) {
    if (!instance[soundName] || !instance[soundName].ready)
      await this._waitCustomInstanceLoad(soundName, instance) // 読み込み待ち
    if (this.isEffectMuted) return
    instance[soundName].sound.play()
  }

  stopCustomSound(soundName, instance) {
    instance[soundName].sound.stop()
  }

  setMute(type, isMuted) {
    if (type === 'bgm') {
      this.stopAll('bgm')
      this.isBGMMuted = isMuted
    } else if (type === 'effect') {
      this.stopAll('effect')
      this.isEffectMuted = isMuted
    }
  }
}

export default new SoundService()
